//#region header */
/**************************************************************************************************
//
//  Description: Organisation store for managing the state of the organisation details.
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial version.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import { create } from "zustand";
import { OrganisationErrorType } from "../models/organisationErrorType";
import { mountStoreDevtool } from "simple-zustand-devtools";

interface OrganisationQuery {
  organisationItem?: string;
  newOrganisation?: boolean;
  organisationSelected?: string;
  organisationChanged?: boolean;
  organisationNames?: string[];
  disableSave?: boolean;
  validationErrors?: OrganisationErrorType[];
}

interface OrganisationQueryStore {
  organisationQuery: OrganisationQuery;
  setOrganisationItem: (organisationItem: string | undefined) => void;
  setNewOrganisation: (newOrganisation: boolean) => void;
  setOrganisationSelected: (organisationSelected: string | undefined) => void;
  setOrganisationChanged: (organisationChanged: boolean) => void;
  setOrganisationNames: (organisationNames: string[]) => void;
  setDisableSave: (disableSave: boolean) => void;
  setValidationErrors: (validationErrors: OrganisationErrorType[] | undefined) => void;
}

const useOrganisationQueryStore = create<OrganisationQueryStore>((set) => ({
  organisationQuery: {},
  setOrganisationItem: (configItem: string | undefined) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, organisationItem: configItem } })),
  setNewOrganisation: (newConfig: boolean) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, newOrganisation: newConfig } })),
  setOrganisationSelected: (configSelected: string | undefined) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, organisationSelected: configSelected } })),
  setOrganisationChanged: (documentChanged: boolean) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, documentChanged } })),
  setOrganisationNames: (organisationNames: string[]) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, organisationNames } })),
  setDisableSave: (disableSave: boolean) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, disableSave } })),
  setValidationErrors: (validationErrors: OrganisationErrorType[] | undefined) =>
    set((state) => ({ organisationQuery: { ...state.organisationQuery, validationErrors } })),
}));

if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("Organisation Store", useOrganisationQueryStore);
}

export default useOrganisationQueryStore;
